import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  signIn,
  confirmSignIn,
  confirmResetPassword,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { APIWithLoaderHelper } from '../helpers/apiWithLoaderHelper';
import { URLConstants } from '@app/core/constants/URLConstants';
import { ClientService } from '@app/client/client.service';
import { Site, SitesService } from './sitesService';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';

type UserRole = 'public' | 'administrator';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  isAdmin: boolean;
}

@Injectable({
  providedIn: 'root',
})
/**
 * Call cognito.
 */
export class AuthService {
  private user: User | null = null;
  originIdentifier: string;

  constructor(
    private apiWithLoaderHelper: APIWithLoaderHelper,
    private clientService: ClientService,
    private sitesService: SitesService,
    private $http: HttpClient
  ) {
    this.originIdentifier = this.clientService.getClientOriginIdentifier();
  }

  login(user: any): Observable<any> {
    return from(signIn({ username: user.email, password: user.password }));
  }

  newPassword(newPassword: string): Observable<any> {
    return from(confirmSignIn({ challengeResponse: newPassword }));
  }

  getResetPasswordCode(email: string): Observable<any> {
    return this.$http.post(environment.apiBaseURL + '/auth/forgot-password', {
      ClientId: environment.amplifyAuthConfig.userPoolClientId,
      Username: email,
    });
  }

  resetPassword(email: string, code: string, newPassword: string): Observable<any> {
    return from(confirmResetPassword({ username: email, confirmationCode: code, newPassword }));
  }

  getAuthenticatedData(): Observable<any> {
    return from(getCurrentUser().catch((e) => null));
  }

  getLoggedInUser(): Observable<{ user: User | null; sites: Site[] | null; forbidden: boolean | null }> {
    return this.getAuthenticatedData().pipe(
      mergeMap((authData) => {
        if (!authData) {
          return of({ user: null, sites: null, forbidden: false });
        }

        return this.apiWithLoaderHelper
          .get<{ user: User; sites: Site[] }>(
            URLConstants.getCurrentUser,
            { queryStringParameters: { origin_identifier: this.originIdentifier } },
            false
          )
          .pipe(
            map((x) => {
              this.sitesService.checkAvailableSites(x.sites);
              this.sitesService.updateSitesData(x.sites);
              return { user: x.user, sites: x.sites, forbidden: false };
            }),
            catchError((): Observable<{ user: User | null; sites: Site[] | null; forbidden: boolean | null }> => {
              return of({ user: null, sites: null, forbidden: true });
            })
          );
      })
    );
  }

  federatedLogin(customProvider: string) {
    return from(signInWithRedirect({ provider: { custom: customProvider } }));
  }

  logout(): Observable<any> {
    this.user = null;
    this.sitesService.clearSelectedSite();
    return from(signOut());
  }
}
